import { count } from "./format";
import type { AppLimit, AppLimitScope, AppPushBacklog, AppUsageDay, RateWindow } from "./types";

/**
 * The pure half of the Apps pages: the limit editor's draft, and the usage table.
 *
 * Mirrors `server/src/api/routes/apps.ts`'s bounds so a row the server would
 * refuse is marked before the request, not after it.
 */

/** The server's ceiling on one limit. */
export const LIMIT_MAX = 1_000_000_000;
export const APP_NAME_MAX = 80;
export const APP_DESCRIPTION_MAX = 500;
export const KEY_LABEL_MAX = 60;
/** `server/src/api/routes/apps.ts`'s bounds on a session push (docs/session-push-plan.md §6). */
export const PUSH_URL_MAX = 500;
export const PUSH_TOKEN_MAX = 2000;

/** `undefined` when the server would take it. `https:` only: the token rides in a header. */
export function validatePushUrl(value: string): string | undefined {
  const text = value.trim();
  if (text === "") return "Required";
  if (text.length > PUSH_URL_MAX) return `At most ${PUSH_URL_MAX} characters`;
  let url: URL;
  try {
    url = new URL(text);
  } catch {
    return "Not a URL";
  }
  if (url.protocol !== "https:") return "Must start with https://";
  if (url.username !== "" || url.password !== "") {
    return "No user or password; put the secret in the token";
  }
  if (url.search !== "" || url.hash !== "") {
    return "No query or fragment; put any secret in the token";
  }
  return undefined;
}

/**
 * Whether saving `url` needs a token typed in. The server keeps a stored token
 * only while the URL stays on its origin, so it is never sent to a host it was
 * not entered for.
 */
export function pushTokenRequired(
  stored: { readonly url: string; readonly tokenSet: boolean } | null,
  url: string,
): boolean {
  if (stored === null || !stored.tokenSet) return true;
  try {
    return new URL(stored.url).origin !== new URL(url.trim()).origin;
  } catch {
    return true;
  }
}

/** A limit row as the editor holds it: `max` is what was typed. */
export interface LimitDraftRow {
  readonly scope: AppLimitScope;
  readonly window: RateWindow;
  readonly max: string;
}

export function draftFromLimits(limits: readonly AppLimit[]): LimitDraftRow[] {
  return limits.map((limit) => ({ ...limit, max: String(limit.max) }));
}

/** One message per row, `undefined` where the row is fine. */
export function validateLimitDraft(rows: readonly LimitDraftRow[]): (string | undefined)[] {
  const seen = new Set<string>();
  return rows.map((row) => {
    const key = `${row.scope}|${row.window}`;
    const duplicate = seen.has(key);
    seen.add(key);

    const text = row.max.trim();
    if (!/^\d+$/.test(text)) return "A whole number of requests";
    const value = Number(text);
    if (value < 1) return "At least 1";
    if (value > LIMIT_MAX) return `At most ${LIMIT_MAX.toLocaleString("en-US")}`;
    if (duplicate) return "This scope already has a limit for that window";
    return undefined;
  });
}

/** Only call on a draft `validateLimitDraft` passed. */
export function limitsFromDraft(rows: readonly LimitDraftRow[]): AppLimit[] {
  return rows.map((row) => ({ scope: row.scope, window: row.window, max: Number(row.max.trim()) }));
}

export function scopeLabel(scope: AppLimitScope): string {
  return scope === "*" ? "All requests" : scope;
}

export interface DailyTotal {
  readonly day: string;
  readonly allowed: number;
  readonly denied: number;
  /** Answered requests per permission. */
  readonly byScope: Readonly<Record<string, number>>;
}

/** Every permission's counts folded into one row per quota day, newest first. */
export function dailyTotals(usage: readonly AppUsageDay[]): DailyTotal[] {
  const byDay = new Map<string, DailyTotal>();
  for (const entry of usage) {
    const current = byDay.get(entry.day) ?? { day: entry.day, allowed: 0, denied: 0, byScope: {} };
    byDay.set(entry.day, {
      day: entry.day,
      allowed: current.allowed + entry.allowed,
      denied: current.denied + entry.denied,
      byScope: {
        ...current.byScope,
        [entry.scope]: (current.byScope[entry.scope] ?? 0) + entry.allowed,
      },
    });
  }
  return [...byDay.values()].sort((a, b) => b.day.localeCompare(a.day));
}

/** One line for what an App has not been sent, and how many "Push all unpushed" would queue. */
export function pushBacklogSummary(backlog: AppPushBacklog): {
  readonly total: number;
  readonly text: string;
} {
  const total = backlog.unsent + backlog.parked;
  if (total === 0) return { total, text: "Every closed session has been queued." };
  const parts = [
    backlog.unsent === 0
      ? undefined
      : `${count(backlog.unsent)} closed session${backlog.unsent === 1 ? "" : "s"} never sent`,
    backlog.parked === 0 ? undefined : `${count(backlog.parked)} stuck`,
  ].filter((part) => part !== undefined);
  return { total, text: parts.join(" · ") };
}
